// Verified, jurisdiction-specific content for priority country pages.
// Sourced from official registries (IRS/Secretary of State, HK Companies Registry, ACRA)
// as of 2026. Government fees are payable to the relevant authority, not to UNICORP.
export const COUNTRY_FACTS = {
  unitedstates: {
    suits: "Non-US founders who need a US LLC to serve US customers, access US payment processors and hold a US business account. It is not suitable as tax or legal advice — a US entity can create US filing and, in some cases, tax obligations depending on your activity.",
    decisions: [
      "Which state to register in — your home/operating state is usually simplest. Forming in Wyoming or Delaware while operating in another state often means registering again as a 'foreign LLC' there and paying fees twice.",
      "Registered agent — mandatory in almost every state; must have a physical in-state address.",
      "EIN (federal tax ID) — needed for banking and IRS filings; issued only after the state approves the LLC.",
      "Beneficial Ownership (BOI) reporting with FinCEN where applicable.",
    ],
    includes: [
      "Preparing and filing your Articles of Organization with the Secretary of State",
      "Registered agent for the first year (where your package specifies)",
      "Assistance preparing your EIN application",
      "Guidance on the documents and information required",
    ],
    excludes: [
      "State filing fees (typically US$35–US$500, set by each state) — payable to the state",
      "IRS decisions and processing times for your EIN",
      "State annual report or franchise-tax obligations",
      "US federal/state tax advice or tax returns",
      "Bank account approval — decided by the bank",
    ],
    documents: ["Passport / photo ID for each member", "Residential address proof", "2–3 proposed company names", "Ownership split and intended business activity"],
    stages: [
      "Name availability check for your chosen state",
      "Articles of Organization filed with the Secretary of State (their decision, their timeline)",
      "EIN application to the IRS after the LLC is approved",
      "Introduction to a banking option (the bank makes its own decision)",
    ],
    timelineNote: "State processing ranges from same-day to a few weeks depending on the state and workload. The EIN can only be obtained after the state approves the company.",
    ongoing: ["State annual report / franchise tax (varies by state)", "FinCEN Beneficial Ownership report where applicable", "US federal and state tax filings as relevant to your activity"],
    refs: [
      { label: "Apply for an EIN free at IRS.gov", href: "https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online" },
      { label: "FinCEN Beneficial Ownership Information", href: "https://www.fincen.gov/boi" },
    ],
    govFee: "Your all-inclusive package covers the state filing fee — we prepare, file and remit it on your behalf, and the annual renewal covers ongoing registered-office and filing charges. An EIN is free if you apply directly with the IRS using the official link above, and you are free to deal with these authorities yourself.",
  },
  hongkong: {
    suits: "Founders trading with Asia and Greater China who want a well-known, low-friction jurisdiction and access to Hong Kong banking. It is not tax advice — a Hong Kong company must keep proper records and file audited accounts.",
    decisions: [
      "Company name (English and/or Chinese) and its availability.",
      "Director(s) — at least one natural-person director; no residency or nationality restriction.",
      "Company secretary — mandatory; must be a Hong Kong resident or a licensed corporate service provider. A sole director cannot also be the secretary.",
      "Registered office — a physical Hong Kong address (PO boxes are not accepted).",
      "Significant Controllers Register (SCR) kept at the registered office.",
    ],
    includes: [
      "Preparing Form NNC1 and the Articles of Association",
      "Electronic submission via the Companies Registry e-Registry",
      "Business Registration application (filed together with incorporation)",
      "Company secretary and registered office for the first year (where your package specifies)",
    ],
    excludes: [
      "Government fees — HK$1,545 incorporation + HK$2,350 one-year Business Registration Certificate (HK$3,895 total), payable to the authorities",
      "Bank account approval — decided by the bank",
      "Audit, accounting and profits-tax filing",
    ],
    documents: ["Certified passport copy and residential address proof for each director and shareholder", "2–3 proposed company names", "Intended business activity and share allocation"],
    stages: [
      "Name and eligibility check",
      "NNC1 + IRBR1 filed via the e-Registry (Companies Registry decision)",
      "Certificate of Incorporation and Business Registration Certificate issued together on approval",
      "Introduction to a banking option (the bank makes its own decision)",
    ],
    timelineNote: "Electronic applications are often processed within about one hour to a few working days. Certifying identity documents abroad can add time.",
    ongoing: ["Annual Return (NAR1) filing", "Business Registration Certificate renewal", "Keeping the Significant Controllers Register up to date", "Audited accounts and profits-tax return", "Maintaining a company secretary"],
    refs: [
      { label: "Hong Kong Companies Registry — register a company", href: "https://www.cr.gov.hk/en/services/register-company.htm" },
    ],
    govFee: "Your all-inclusive package covers the HK$3,895 government incorporation and Business Registration fees — we prepare, file and remit them on your behalf. You are also free to file directly through the Companies Registry e-Registry.",
  },
  singapore: {
    suits: "Founders wanting a reputable regional base in Asia with strong banking. It is not tax advice — a Pte Ltd carries ongoing ACRA and tax-filing obligations, and foreign founders must meet the resident-director and filing-agent rules.",
    decisions: [
      "Company name and its ACRA approval.",
      "At least one director ordinarily resident in Singapore — if no founder qualifies, a nominee director service can be used.",
      "Company secretary — a Singapore-resident natural person, appointed within six months; the sole director cannot also be the secretary.",
      "Paid-up capital — minimum S$1 (higher amounts can signal substance to banks).",
      "Registered office — a physical Singapore address (no PO boxes).",
    ],
    includes: [
      "Name reservation and ACRA filing via a licensed filing agent (foreign founders without Singpass cannot self-file)",
      "Register of Registrable Controllers (RORC) filing",
      "Nominee director and company secretary / registered office where your package specifies",
    ],
    excludes: [
      "Government fees — S$15 name reservation + S$300 registration (S$315 total), payable to ACRA",
      "Bank account approval — decided by the bank",
      "Audit, accounting, corporate-tax and GST filing",
    ],
    documents: ["Passport and residential address proof for each officer and shareholder", "2–3 proposed company names and intended activity (SSIC)", "Shareholding structure"],
    stages: [
      "Name reservation via BizFile+ (valid 120 days)",
      "Incorporation filed by the licensed filing agent (ACRA decision)",
      "Incorporation typically completes in 1–3 business days; RORC filed on the first day",
      "Introduction to a banking option (the bank makes its own decision)",
    ],
    timelineNote: "Once documents and KYC are complete, incorporation usually takes 1–3 business days. Name approval and KYC can add time.",
    ongoing: ["Annual Return filing and AGM / financial statements", "Corporate-tax and (if registered) GST filings", "Maintaining a resident director, company secretary and RORC"],
    refs: [
      { label: "ACRA — registering a local company", href: "https://www.acra.gov.sg/register/business/registering-different-business-structures/local-company" },
    ],
    govFee: "Your all-inclusive package covers the S$315 ACRA fees (name reservation + registration), plus any nominee/secretary services you select — we prepare, file and remit them on your behalf. Foreign founders are required by ACRA to use a licensed filing agent.",
  },
};
